import { useState } from 'react';

import { api, ApiError } from '../../../api/client';
import { SocialAuthButtons } from '../../auth/SocialAuthButtons';
import type { RegistrationDraft, RegistrationRole } from '../model';
import { Button, Input, WizardButton } from '../../../ui';

export interface RegistrationEmailStepProps {
  role: RegistrationRole;
  draft: RegistrationDraft;
  updateDraft: (patch: Partial<RegistrationDraft>) => void;
  onExistingUser: () => void;
  onNewUser: () => void;
  onSwitchRole: () => void;
  figmaNode?: string;
}

export function RegistrationEmailStep({
  role,
  draft,
  updateDraft,
  onExistingUser,
  onNewUser,
  onSwitchRole,
  figmaNode,
}: RegistrationEmailStepProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isCustomer = role === 'customer';

  const next = async () => {
    const email = draft.email.trim();
    if (!email) return setError('Введите email.');
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
      return setError('Введите корректный email латиницей (например: name@example.com).');
    }
    setBusy(true);
    setError('');
    try {
      const result = await api<{ exists: boolean }>('/api/auth/start/', {
        method: 'POST',
        body: { email },
      });
      updateDraft({ targetRole: role });
      if (result.exists) {
        onExistingUser();
      } else {
        onNewUser();
      }
    } catch (nextError) {
      setError(nextError instanceof ApiError ? nextError.message : 'Не удалось проверить email');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="standalone-auth__registration-form standalone-auth__registration-form--email"
      data-figma-node={figmaNode}
    >
      <div className="standalone-auth__copy standalone-auth__copy--tall">
        <h1>{isCustomer ? 'Хотите найти перевозчика — начнём' : 'Готовы возить — начнём'}</h1>
        <p>
          Введите email. Если аккаунт уже есть — попросим пароль. Если нет — за пару минут создадим, потом верификация и поехали.
        </p>
      </div>

      <div className="standalone-auth__email-fields">
        <div className="request-wizard__role-banner">
          <span>
            Регистрируетесь как: <strong>{isCustomer ? 'Заказчик' : 'Перевозчик'}</strong>
          </span>
          <Button
            icon="right"
            kind="tertiary"
            size="medium"
            type="button"
            onClick={onSwitchRole}
          >
            {isCustomer ? 'Я перевозчик' : 'Я заказчик'}
          </Button>
        </div>

        <Input
          autoComplete="email"
          density="compact"
          error={error}
          label="Email"
          placeholder={isCustomer ? 'anna@example.com' : 'dmitry@example.com'}
          type="email"
          value={draft.email}
          onChange={(event) => {
            setError('');
            updateDraft({ email: event.target.value, targetRole: role });
          }}
        />
      </div>

      <WizardButton disabled={busy} type="button" onClick={() => void next()}>
        {busy ? 'Проверяем…' : 'Продолжить'}
      </WizardButton>

      <div className="standalone-auth__social-block">
        <div className="request-wizard__divider">
          <span>или войти через</span>
        </div>
        <SocialAuthButtons role={role} flow="registration" />
        <p>
          Продолжая, вы соглашаетесь с <u>условиями оферты</u>, <u>обработкой персональных данных</u> и <u>офертой перевозчика</u>
        </p>
      </div>
    </div>
  );
}
